import { Info } from 'lucide-react';

/**
 * La réserve « ce montant est une estimation », posée à côté des chiffres.
 *
 * POURQUOI CE COMPOSANT EXISTE. Le mot « estimation » n'apparaissait qu'UNE fois dans
 * toute l'application, au bas de l'écran Survoltage — mesuré le 30/08/2026, reconstaté
 * le 31/08 sur le bundle servi par staging ET par la pré-production. Les 50 autres
 * montants de revenu s'affichaient comme des faits. Or aucun de ces chiffres n'est un
 * fait : ce sont des forfaits de barème mis à l'échelle de l'état du parc et de la part
 * opérateur, sur des mécanismes dont les prix bougent. David les lit devant son client.
 *
 * UNE SEULE SOURCE POUR LA PHRASE. Onze blocs portent la réserve ; s'ils portaient onze
 * phrases écrites à la main, elles divergeraient au premier remaniement et l'une d'elles
 * finirait par promettre plus que les autres. Le texte vit donc ici, et nulle part
 * ailleurs.
 *
 * ⚠️ SECONDE PASSE, 31/08 : LA MENTION SEULE NE SUFFIT PAS, et c'est le retour de
 * Boubacar après la mise en pré-production. On lit un grand chiffre et son intitulé ; on
 * ne lit pas la ligne grise en dessous. Le mot vit désormais AUSSI dans les intitulés
 * eux-mêmes — « Rémunération opérateur estimée », « Revenu brut annuel estimé » — et
 * `test_revenu_titres_estimes.py` recense les cinquante-deux libellés concernés. Cette
 * mention-ci reste : elle dit ce qu'un titre ne peut pas dire, c'est-à-dire SUR QUOI
 * l'estimation repose et ce qui peut la faire varier.
 *
 * ELLE A GROSSI en même temps : 12,5 px au lieu de 11, romaine au lieu d'italique, et le
 * mot « Estimation » en gras. Une réserve qu'on doit chercher n'est pas une réserve.
 *
 * DISCRÈTE NE VEUT PAS DIRE INVISIBLE, ET PLUS GROSSE NE VEUT PAS DIRE ALARMANTE : pas
 * de fond d'alerte, pas de rouge. Un bandeau rouge sur chaque écran de revenus se ferait
 * ignorer en trois jours, et ferait douter des montants qui, eux, sont certains — un
 * versement perçu, une facture émise.
 *
 * CE QUI NE DOIT JAMAIS LA PORTER : les montants réellement versés (colonne « Réel
 * versé »), les montants de facture. Ce sont des faits ; les couvrir d'une réserve
 * serait la même faute, dans l'autre sens. `test_revenu_estimatif.py` garde les deux
 * bords, et `test_revenu_titres_estimes.py` garde que leurs intitulés restent intacts.
 */

// Ce qui fonde l'estimation, par source de calcul. La phrase nomme le barème : sans lui,
// « estimation » ne dit pas de quoi le chiffre dépend, donc ne dit rien d'actionnable.
// Chaque texte est la SUITE du mot « Estimation », qui est rendu en gras devant.
//
// ⚠️ `survoltage` VAUT `null`, ET CE N'EST PAS UN OUBLI — décision de Boubacar du
// 14/09/2026, après qu'il a demandé le retrait de la référence « barème juillet 2026 »
// partout où elle s'affiche. Les six blocs Survoltage ne portent plus que le mot
// « Estimation », sans suite.
//
// ⚠️ ET `null` N'EST PAS LA MÊME CHOSE QU'UNE CLÉ ABSENTE, c'est tout l'objet de cette
// valeur. Le rendu retombait sur `FONDEMENTS.parc` pour toute source inconnue : une clé
// simplement supprimée aurait donc fait dire à un bloc Survoltage « ces montants dérivent
// des barèmes Survoltage ET Symphonics appliqués au parc réel », ce qui est faux pour lui.
// `null` dit « cette source se tait volontairement » et se distingue de « cette source
// n'est pas prévue », qui garde son repli.
//
// CE QUE LE RETRAIT COÛTE, écrit ici plutôt que redécouvert : la réserve Symphonics juste
// à côté, elle, nomme toujours son barème. Deux agrégateurs sont donc traités
// différemment sur le même écran. C'est assumé, pas oublié.
const FONDEMENTS = {
  survoltage: null,
  symphonics:
    'selon le barème Symphonics enregistré, après application de la part opérateur — '
    + "le montant réellement versé peut s'en écarter.",
  // ⚠️ LA RÉSERVE DU PARC NE NOMME AUCUN AGRÉGATEUR, et ce n'est pas un oubli.
  // Retour de David en vidéo, le 24/09 : « au lieu de Symphonics, Survoltage, tu mets
  // agrégateur ». Elle parle du parc entier, et la liste des agrégateurs n'est pas
  // figée : TILT a été ajouté à l'écran sans que cette phrase le sache. Les réserves
  // `survoltage` et `symphonics` ci-dessus, elles, parlent d'UN barème et le nomment.
  // `test_retours_loom_david.py` garde les deux bords.
  parc:
    '— ces montants dérivent des barèmes des agrégateurs appliqués au parc réel. '
    + 'Ils ne constituent ni un revenu perçu ni un montant facturé.',
};

/**
 * @param {'survoltage'|'symphonics'|'parc'} source  le barème qui fonde le montant
 * @param {string} bloc  identifiant du bloc couvert ; sert le `data-testid` et le
 *                       recensement du lot N (`test_revenu_estimatif.py`)
 * @param {string} className  marges laissées à l'appelant, la mention n'en pose aucune
 */
export default function MentionEstimation({ source = 'parc', bloc, className = '' }) {
  const fondement = Object.hasOwn(FONDEMENTS, source) ? FONDEMENTS[source] : FONDEMENTS.parc;
  return (
    <p
      className={`flex items-start gap-1.5 text-[12.5px] leading-relaxed text-neutral-600 ${className}`}
      data-testid={`mention-estimation-${bloc}`}
    >
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500" aria-hidden="true" />
      <span>
        <strong className="font-semibold text-neutral-700">Estimation</strong>
        {/* `Object.hasOwn` et non `||` : une source declaree a `null` se TAIT, une source
            inconnue retombe sur `parc`. Le `||` confondait les deux et aurait fait dire a
            un bloc Survoltage la phrase du parc, qui parle des DEUX baremes. */}
        {fondement ? ' ' : ''}
        {fondement}
      </span>
    </p>
  );
}

export { FONDEMENTS };
